import { COOKIE_IS_GUEST_USER, COOKIE_LOGIN_TOKEN, WHOLESALE_NO_LOGIN_PAGES } from '../config/constant';
import { setCookie, getCookie, delCookie } from 'ava-util/dist/cookie';
import Message from '@base/lib/Message';
import { startTrack, trackRegister } from 'base/utils/tracker';
import { getInitInfo, quickRegister } from 'base/servicesV2/user';
import { IInitInfo, IQuickRegisterParams } from 'base/interfacesV2/user';
import { getUnReadCount } from 'base/services/message';
import { syncGlobalStore } from 'base/store';
import site from 'pro/config/site';
import { RouteEnum } from 'pro/config/routers';
import router from 'next/router';
import { generatePath } from 'ava-util/dist/request';
import { isStringIncludedArrayString } from 'base/utils/string';

export type UserStateType = IInitInfo & {
    loaded: boolean; // 进入页面后第false，/init/info 加载完成后设为true
    loginToken: string; // 从登陆注册接口或cookie获取
};

export type UserStoreType = {
    state: UserStateType;
    update: (newState: Partial<UserStateType>) => void;//更新state
    checkAuth: () => boolean;//判断是否是已登录状态
    fetchUserInfo: (params?: { from?: string; }) => Promise<Partial<UserStateType>>;//获取用户信息
    login: (newState: Partial<UserStateType>) => Promise<void>;//登录
    logout: () => void;//登出
    quickRegister: (params: IQuickRegisterParams) => Promise<void>;
    isDealer: () => boolean; //判断是不是大客户
    isDropShipping: () => boolean; //判断是不是 DROP_SHIPPING 用户
    isInfoLoaded: () => boolean; //判断请求完成
};

const getGuestState = (): UserStateType => ({
    loaded: false,
    loginToken: '',
    accessToken: '',
    cartId: '',
    cartNum: 0,
    messageNum: 0,  // 未读消息条数
    couponNum: 0,
    currentTime: 0,
    email: '',
    isGuestUser: false,
    isLogin: false,
    payVerifyEmail: '',
    sha1Email: '',
    track: true,
    userId: '0',
    userName: 'guest',
    referralCode: '',
    userTags: [],
    pointsRule: {
        consumePoints: '',
        consumePointsPrice: '',
        obtainPoints: '',
        obtainPointsPrice: '',
    },
});

// 未读消息
const unreadMessageHandle = async (): Promise<{
    messageNum: number;
}> => {
    try {
        const res = await getUnReadCount()
        const unRead = res?.messageTypeWithUnRead?.reduce((preItem, curItem) => {
            return {
                ...curItem,
                unReadNum: curItem.unReadNum + preItem.unReadNum
            }
        })
        return { messageNum: unRead.unReadNum || 0 }
    } catch (err) {
        console.log('[userStore getUnReadCount error]', err)
        return { messageNum: 0 }
    }
}

const createUserStore = (initState?: Partial<UserStateType>): UserStoreType => ({
    state: {
        ...getGuestState(),
        ...initState,
    },
    update(newState) {
        for (const key in newState) {
            this.state[key] = newState[key];
        }
    },
    checkAuth() {
        // 匿名用户判断为 不登录状态
        const isGuestUser = (process as any).browser
            ? !!getCookie(COOKIE_IS_GUEST_USER)
            : this.state.isGuestUser
        return isGuestUser
            ? false
            : !!((process as any).browser
                ? getCookie(COOKIE_LOGIN_TOKEN)
                : this.state.loginToken);
    },
    async fetchUserInfo(params) {
        const isServer = !(process as any).browser;
        try {
            const [userRes, messageRes] = await Promise.all([
                getInitInfo(),
                unreadMessageHandle()
            ]);

            const isDealer = !!userRes.userTags.find(tag => tag.toLowerCase() === 'dealer');
            const pathname = window.location.pathname

            if (site.project === 'vivosun' && isDealer) {
                // 已登录WholeSale账号通过 url 访问主站，自动登出并刷新
                this.logout();
                if (!isServer) {
                    router.reload();
                }
                return this.state;
            } else if (site.project === 'vs-ws'
                && !isStringIncludedArrayString(pathname, WHOLESALE_NO_LOGIN_PAGES)
                && !(userRes.isLogin && isDealer)
            ) {

                // 已登录普通账号通过 url 访问WholeSale站，自动登出并跳转登录注册
                if (userRes.isLogin) {
                    this.logout();
                }
                const isLoginPage = params?.from === 'loginPage' || pathname.startsWith(RouteEnum.LOGIN);
                if (!isLoginPage) {
                    router.push(generatePath(RouteEnum.LOGIN, { redirect: window.location.href.replace(window.location.origin, '') }));
                }
                return this.state;
            }

            // userRes.userTags = ['DROP_SHIPPING', 'DEALER']; //Mock
            const newState = {
                ...userRes,
                ...messageRes,
                loginToken: (userRes.isGuestUser || userRes.isLogin)
                    ? (getCookie(COOKIE_LOGIN_TOKEN) || '')
                    : '',
                loaded: true,
                // currentTime: ((new Date('2023-11-20 00:00:00').getTime()) / 1000),
            };
            if (!isServer) {
                // 不是匿名用户 未登录 fetchUserInfo 删除匿名
                if (!newState.loginToken) {
                    delCookie(COOKIE_LOGIN_TOKEN);
                }
                if (!newState.isGuestUser) {
                    delCookie(COOKIE_IS_GUEST_USER)
                }

                startTrack({
                    userId: userRes.userId || '0',
                    email: userRes.email || '',
                    sha1Email: userRes.sha1Email || '',
                    cartId: userRes.cartId,
                    userTags: userRes.userTags,
                });
            }

            this.update(newState);
            syncGlobalStore('userStore', newState);
            return newState;

        } catch (e) {
            if (isServer) {
                console.log('[userStore fetchUserInfo error]', e);
            } else {
                Message.error(e.message);
            }
        }
        return {}
    },
    async login(newState) {
        if (newState.loginToken) {
            setCookie(COOKIE_LOGIN_TOKEN, newState.loginToken);
        }
        if (newState.isGuestUser) {
            setCookie(COOKIE_IS_GUEST_USER, +newState?.isGuestUser)
        } else {
            delCookie(COOKIE_IS_GUEST_USER)
        }
        // 排除匿名用户
        this.update({
            ...newState,
            isLogin: !newState?.isGuestUser || true,
        });
    },
    logout() {
        delCookie(COOKIE_LOGIN_TOKEN);
        delCookie(COOKIE_IS_GUEST_USER);
        this.update({
            isLogin: false,
            userName: 'guest',
            loginToken: '',
            isGuestUser: false,
        });
    },
    async quickRegister(params) {
        try {
            const res = await quickRegister(params)
            trackRegister();
            this.login(res);
            this.fetchUserInfo();
        } catch (e) {
            throw new Error(e.message);
        }
    },
    isDealer() {
        return this.state.userTags.includes('DEALER');
    },
    isDropShipping() {
        return this.state.userTags.includes('DROP_SHIPPING');
    },
    isInfoLoaded() {
        return this.state.loaded
    }
});

export default createUserStore;
